import React, { useState, useRef, useEffect } from "react";
import { RxCross1 } from "react-icons/rx";
import { FaCropSimple } from "react-icons/fa6";
import { MdOutlineDeleteOutline } from "react-icons/md";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { deleteApi, getApi, postApi, putApi } from "../AppApis";
import config from "../../config";
import { toast } from "react-toastify";

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
    return centerCrop(makeAspectCrop({ unit: "%", width: 90 }, aspect, mediaWidth, mediaHeight), mediaWidth, mediaHeight);
}

export default function PictureManagement({ selectedImage, onClose, setIsDeleteModalShow, imageFiles, setImageFiles, property }) {
    const [crop, setCrop] = useState();
    const [isCropImage, setIsCropImage] = useState(false);
    const [croppedBlob, setCroppedBlob] = useState(null);
    const [label, setLabel] = useState(selectedImage.label || "");
    const [description, setDescription] = useState(selectedImage.LongDescription || "");
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [localImageUrl, setLocalImageUrl] = useState(null);
    const imgRef = useRef(null);

    useEffect(() => {
        const convertToLocalBlobUrl = async (imageUrl) => {
            try {
                const response = await fetch(imageUrl, { mode: "cors" });
                const blob = await response.blob();
                return URL.createObjectURL(blob);
            } catch (err) {
                console.error("Failed to convert image to local blob:", err);
                return null;
            }
        };

        const loadImage = async () => {
            const localUrl = await convertToLocalBlobUrl(selectedImage.image_url);
            setLocalImageUrl(localUrl);
        };
        loadImage();
    }, [selectedImage]);

    const getCroppedImg = async (image, crop) => {
        const canvas = document.createElement("canvas");
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        canvas.width = crop.width;
        canvas.height = crop.height;
        const ctx = canvas.getContext("2d");

        try {
            ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY, 0, 0, crop.width, crop.height);

            // Security test
            ctx.getImageData(0, 0, 1, 1);

            return new Promise((resolve) => {
                canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9);
            });
        } catch (err) {
            console.warn("Canvas export failed:", err);
            return null;
        }
    };

    const handleSaveCrop = async () => {
        setError(null);
        if (imgRef.current && crop) {
            try {
                const blob = await getCroppedImg(imgRef.current, crop);
                if (!blob) {
                    setError("This image can't be cropped due to security restrictions.");
                    return;
                }
                setCroppedBlob(blob);
                setIsCropImage(false);
            } catch (err) {
                console.error("Crop error:", err);
                setError("Failed to crop image.");
                setIsCropImage(false);
            }
        }
    };

    const onSave = async () => {
        setIsSaving(true);
        setError(null);
        const IMG_BASE_URL = config.get("IMAGE_BASE_URL");

        try {
            const formData = new FormData();

            if (croppedBlob) {
                const file = new File([croppedBlob], selectedImage.name || `image-${Date.now()}.jpg`, { type: "image/jpeg" });
                formData.append("new_image", file);
            } else {
                // Create file from image_url when only updating label/description
                const response = await fetch(selectedImage.image_url, { mode: "cors" });
                const blob = await response.blob();
                const fileName = selectedImage.image_url.split(`${IMG_BASE_URL}`)[1] || `image-${Date.now()}.jpg`;
                const file = new File([blob], fileName, { type: blob.type || "image/jpeg" });
                formData.append("new_image", file);
            }

            formData.append("label", label || "N/A");
            formData.append("description", description || "N/A");
            formData.append("media_key", selectedImage.MediaKey);
            formData.append("property_id", property?.property_id);

            await postApi(`/properties/edit-property-image`, formData, true);
            const getSingleProperty = await getApi(`/properties/get_public_property/${property?.property_id}`);

            if (getSingleProperty?.response && getSingleProperty?.response?.data) {
                const propertyData = getSingleProperty?.response?.data;
                setImageFiles(propertyData?.images.map((item) => ({ ...item })));
            }
            toast.success("Image updated successfully! When published, it will be available to view.");

            onClose();
        } catch (err) {
            console.error("Save failed:", err);
            setError("Failed to save changes. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    function onImageLoad(e) {
        const { width, height } = e.currentTarget;
        setCrop(centerAspectCrop(width, height, 16 / 9));
    }

    const getDisplayImage = () => {
        if (isCropImage && localImageUrl) return localImageUrl;
        if (croppedBlob) return URL.createObjectURL(croppedBlob);
        return selectedImage.image_url;
    };

    return (
        <div className="tw-fixed tw-inset-0 tw-flex tw-justify-center tw-items-center tw-bg-black/50" style={{ zIndex: 9999 }}>
            <div className="tw-bg-white tw-rounded-md tw-p-5 tw-w-[90vw] md:tw-w-[600px] tw-max-h-[90vh] tw-overflow-y-auto">
                <div className="tw-flex tw-justify-between tw-items-center">
                    <p className="tw-font-bold tw-text-[25px] tw-text-black">Edit Image</p>
                    <button onClick={onClose} className="tw-text-gray-500 hover:tw-text-gray-700" disabled={isSaving}>
                        <RxCross1 size={20} />
                    </button>
                </div>

                {error && <div className="tw-mt-2 tw-p-2 tw-bg-red-100 tw-text-red-700 tw-rounded">{error}</div>}

                <div className="tw-mt-4">
                    {isCropImage ? (
                        <ReactCrop crop={crop} onChange={setCrop} aspect={16 / 9} className="tw-max-h-[400px] tw-overflow-hidden">
                            <img
                                ref={imgRef}
                                // crossorigin="anonymous"
                                src={getDisplayImage()}
                                className="tw-w-full tw-rounded-md tw-object-contain"
                                onLoad={onImageLoad}
                                alt="property"
                            />
                        </ReactCrop>
                    ) : (
                        <img
                            src={getDisplayImage()}
                            // crossorigin="anonymous"
                            className="tw-w-full tw-rounded-md tw-max-h-[400px] tw-object-contain"
                            alt="property"
                        />
                    )}
                </div>

                <div className="tw-w-full tw-flex tw-mt-4 tw-justify-end tw-items-center tw-gap-3">
                    <button
                        onClick={() => (isCropImage ? handleSaveCrop() : setIsCropImage(true))}
                        className="tw-bg-gray-100 tw-px-3 tw-py-2 tw-rounded-md tw-flex tw-items-center tw-gap-2 tw-text-black hover:tw-bg-gray-200"
                        disabled={isSaving}
                    >
                        <FaCropSimple />
                        <p>{isCropImage ? "Save Crop" : "Crop Image"}</p>
                    </button>
                    <button
                        onClick={() => setIsDeleteModalShow(selectedImage)}
                        className="tw-bg-gray-100 tw-px-3 tw-py-2 tw-rounded-md tw-flex tw-items-center tw-gap-2 tw-text-black hover:tw-bg-gray-200"
                        disabled={isSaving}
                    >
                        <MdOutlineDeleteOutline className="tw-text-red-500" />
                        <p>Delete</p>
                    </button>
                </div>

                <div className="tw-mt-6">
                    <p className="tw-font-bold tw-text-[20px] tw-text-black">Image Details</p>
                    <input
                        type="text"
                        placeholder="Label"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        className="tw-w-full tw-mt-2 tw-p-2 tw-rounded-md tw-border tw-border-gray-300 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                    />
                    <textarea
                        placeholder="Enter description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="tw-w-full tw-mt-3 tw-h-[120px] tw-p-2 tw-rounded-md tw-border tw-border-gray-300 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                    />
                </div>

                <div className="tw-flex tw-mt-5 tw-justify-center tw-items-center tw-gap-3">
                    <button
                        onClick={onClose}
                        className="tw-border tw-border-gray-300 tw-px-4 tw-py-2 tw-rounded-md tw-text-black hover:tw-bg-gray-100"
                        disabled={isSaving}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onSave}
                        className="tw-bg-blue-600 tw-rounded-md tw-px-4 tw-py-2 tw-text-white hover:tw-bg-blue-700 disabled:tw-opacity-70"
                        disabled={isSaving}
                    >
                        {isSaving ? "Saving..." : "Save Changes"}
                    </button>
                </div>
            </div>
        </div>
    );
}
